import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { Tag, TagColorType } from "components/DesignSystem/Tag/Tag";
import { ArrowUpRight12 } from "components/icons/ArrowUpRight12";
import { InprogressAgentIcon } from "components/icons/InprogressAgentIcon";
import { InprogressDeadlineIcon } from "components/icons/InprogressDeadlineIcon";
import { InprogressPreparerIcon } from "components/icons/InprogressPreparerIcon";
import { TASK_STATE_TO_COLOR } from "dictionaries";
import FilingInprogressIcon from "static/images/FilingInprogressIcon.svg";
import { Task, TaskStateType } from "types/Models/task";
import { formatDate } from "utils/formatDate";
import { openLink } from "utils/openLink";
import TaskButtonBar from "./TaskButtonBar";
import YearlyAdvanceTaskComplete from "static/images/YearlyAdvanceTaskComplete.svg";
import { COMPLETED_STATUS } from "types/Models/taxHome";

export const YearlyAdvanceTask = ({
  task,
  parentRef,
}: {
  task: Task;
  parentRef: any;
}) => {
  const isTaskCompleted =
    (task.state as TaskStateType)?.name === COMPLETED_STATUS ||
    task.state === COMPLETED_STATUS;

  return (
    <div className="taxing t-h-full">
      <div className="t-w-[500px] t-mx-auto t-text-center t-flex t-flex-col t-justify-center t-items-center t-mt-[52px]">
        {isTaskCompleted ? (
          <img
            src={YearlyAdvanceTaskComplete}
            alt="YearlyAdvanceTaskComplete"
          />
        ) : (
          <img src={FilingInprogressIcon} alt="FilingInprogressIllustration" />
        )}
        <div className="t-flex t-flex-col t-gap-6 t-mt-12">
          {isTaskCompleted ? (
            <div className="t-flex t-flex-col t-gap-2">
              <div className="t-text-subtext t-text-text-100">
                All Quarterly Filings Done 🎉
              </div>
              <div className="t-text-body-sm t-text-text-30">
                All quarterly filings for the financial year have been
                successfully completed. You can view the details of each filing
                by clicking on them.
              </div>
            </div>
          ) : (
            <div className="t-flex t-flex-col t-gap-2">
              <div className="t-text-subtext t-text-text-100">
                Filling in progress
              </div>
              <div className="t-text-body-sm t-text-text-30">
                We’ve initiated the advance tax filing task for this year. Each
                quarter will be filed in sequence as deadlines approach. This
                page reflects the latest status across all quarters, and we’ll
                share updates with you in the chat.
              </div>
            </div>
          )}
          {task.bundled_tasks?.length > 0 && (
            <div className="t-flex t-flex-col t-w-full">
              {task?.bundled_tasks?.map((bundledTask, index) => {
                const taskStateType = (bundledTask?.type ||
                  "Not Started") as keyof typeof TASK_STATE_TO_COLOR;

                return (
                  <div
                    key={bundledTask.uuid}
                    className={classNames(
                      "t-flex t-justify-between t-items-center t-gap-4 t-py-3",
                      {
                        "t-border-0 t-border-solid t-border-t t-border-neutral-10":
                          index !== 0,
                      }
                    )}
                  >
                    <span className="t-group">
                      <Button
                        size="small"
                        customType="link"
                        onClick={() =>
                          openLink(`/tax/filings/${bundledTask.uuid}`)
                        }
                      >
                        {bundledTask.name}
                        <span className="t-text-purple group-hover:t-block t-hidden t-ml-1">
                          <ArrowUpRight12 strokeWidth="1" />
                        </span>
                      </Button>
                    </span>
                    <Tag
                      tagType={
                        TASK_STATE_TO_COLOR[taskStateType] as TagColorType
                      }
                      icon={false}
                      size="small"
                      rounded
                    >
                      {bundledTask.state}
                    </Tag>
                  </div>
                );
              })}
            </div>
          )}
          <hr className="t-w-full t-text-neutral-10 t-mx-auto t-my-0" />
          <div className="t-flex t-flex-col t-gap-3.5">
            <div className="t-flex t-justify-between t-items-center">
              <div className="t-flex t-items-center t-gap-1">
                <InprogressAgentIcon />
                <div className="t-text-body-sm t-text-text-30">Agent:</div>
              </div>
              <div className="t-text-text-100 t-text-body-sm">
                {task?.assigned_agent?.name || "-"}
              </div>
            </div>
            <div className="t-flex t-justify-between t-items-center">
              <div className="t-flex t-items-center t-gap-1">
                <InprogressPreparerIcon />
                <div className="t-text-body-sm t-text-text-30">Preparer:</div>
              </div>
              <div className="t-text-text-100 t-text-body-sm">
                {task?.assigned_to?.name || "-"}
              </div>
            </div>
            <div className="t-flex t-justify-between t-items-center">
              <div className="t-flex t-items-center t-gap-1">
                <InprogressDeadlineIcon />
                <div className="t-text-body-sm t-text-text-30">Deadline</div>
              </div>
              <div className="t-text-text-100 t-text-body-sm">
                {task?.deadline ? formatDate(task.deadline) : "-"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <TaskButtonBar justifyContent="between" parentRef={parentRef}>
        <Button type="button" disabled>
          Previous
        </Button>
        <Button customType="primary" disabled type="submit">
          Next
        </Button>
      </TaskButtonBar>
    </div>
  );
};
